import { defineStore } from 'pinia'

import { useWindowSize } from '@shared/composables/use-window-size'
import EventBus, { VideoCallEvents } from '@shared/event-bus'
import { ErrorTypes } from '@shared/models/enums/http'
import { StoreState } from '@shared/models/interfaces/store'
import { IAttachment } from '@shared/models/proto/base/attachment'
import { IFileUpload } from '@shared/models/proto/base/file-upload'
import {
  IMessage,
  IQuickSignDocument,
} from '@shared/models/proto/base/messages'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { mergeAttachmentToEmbeddedAttachment } from '@shared/utils/helpers/attachments/merge-attachment-to-embedded-attachment'
import { isNetworkError } from '@shared/utils/helpers/error-guards/common-errors'
import { createError } from '@shared/utils/helpers/errors/error-builder'
import { isOnline } from '@shared/utils/helpers/general/is-online'
import { mergeMessages } from '@shared/utils/helpers/merge-messages'
import { updateQuickSignInMessage } from '@shared/utils/helpers/messages/update-quick-sign-in-message'
import { useVideoCallNotifications } from '@shared/utils/helpers/video-call-notifications'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { MessageType } from '@protos/chat/message_type_pb'

import { MessagesIndexRO } from '@pc/models/proto/ro/messages'
import {
  CreateMessageData,
  MessageIndexDTO,
} from '@vc/models/proto/dto/messages'
import { MessageIndexRO } from '@vc/models/proto/ro/messages'
import { useConversationsStore } from '@vc/store/conversations'
import { useMessageAttachmentsStore } from '@vc/store/message-attachments'
import { useQuickSignStore } from '@vc/store/quick-sign'
import { useSocketStore } from '@vc/store/socket'
import { useVideoCallStore } from '@vc/store/video-call'

export interface State {
  messages: StoreState<MessageIndexRO>
  sendMessageState: StoreState<{
    data: IMessage
  }>
  loadingExternalLinks: Record<string, boolean>
}

const state = (): State => ({
  messages: getInitialState(),
  loadingExternalLinks: {},
  sendMessageState: getInitialState(),
})

export const apiTransport = new ApiTransport(resourcePath, 'vc')

export const useMessagesStore = defineStore('messages', {
  state,

  actions: {
    async fetchMessages(dto: {
      conversationId: number
      page?: number
      perPage?: number
      chatWidgetId: number
    }) {
      const videoCallStore = useVideoCallStore()
      videoCallStore.isPatientVideoTestFailed = false

      this.messages = updateState(this.messages)
      try {
        const response = await apiTransport.get<
          MessageIndexDTO,
          MessageIndexRO
        >('/messages', new MessageIndexDTO(dto), MessageIndexRO)

        const { meta, data: newMessages } = response ?? { meta: {}, data: [] }
        const { data: existingMessages = [] } = this.messages.data ?? {}

        const byConversationId = (msg: IMessage) =>
          msg.conversationId === dto.conversationId

        const mergedMessages = mergeMessages({
          callback: videoCallStore.checkIsPatientVideoTestFailed,
          array: [existingMessages.filter(byConversationId), newMessages],
        })

        this.messages = updateState(
          this.messages,
          new MessageIndexRO({
            meta,
            data: mergedMessages,
          })
        )
      } catch (error) {
        this.messages = updateState(this.messages, error as Error)
        throw error
      }
    },

    async sendMessage(
      message: Pick<
        CreateMessageData,
        'conversationId' | 'text' | 'messageType' | 'attachments' | 'payload'
      > & { createdAt?: string },
      files: IFileUpload[]
    ): Promise<void> {
      const socketStore = useSocketStore()
      const [file, ...restFiles] = files
      const isQuickSignDocument = message?.attachments.find(
        (item) => item?.quickSignDocument
      )
      const attachments = isQuickSignDocument ? [] : message?.attachments
      const messageType = isQuickSignDocument
        ? MessageType.QUICK_SIGN
        : message.messageType
      if (restFiles.length) {
        throw new Error('Only one file attachment per message is supported')
      }

      const dto = {
        files:
          file && !isQuickSignDocument
            ? [{ ...file, content: new Uint8Array() }]
            : [],
        data: {
          ...message,
          attachments,
          messageType,
          notifyApi: true,
          alert: true,
          epochTime: Math.ceil(new Date().getTime() / 1000),
          createdAt: message.createdAt || new Date().toISOString(),
          specificRecipientId: 0,
        },
      }

      const isOnlineUser = isOnline() && socketStore.isChannelsConnected
      this.sendMessageState = updateState(this.sendMessageState)
      try {
        if (!isOnlineUser) {
          const networkError = createError(ErrorTypes.NETWORK)
          throw networkError
        }

        const socketStore = useSocketStore()
        const newMessage = await socketStore.sendChatMessage(dto)

        if (file) {
          const messageAttachmentsStore = useMessageAttachmentsStore()
          const quickSignStore = useQuickSignStore()
          if (isQuickSignDocument) {
            const annotation =
              message?.attachments[0]?.quickSignDocument?.latestSigner
                ?.annotation || ''
            // start upload attachment
            socketStore.pushFileUploadStarted(
              message.conversationId,
              newMessage.id
            )
            await quickSignStore.createQuickSignDocument({
              file,
              messageId: newMessage.id,
              annotation,
            })
            // finish upload attachment
            socketStore.pushFileUploadFinished(
              message.conversationId,
              newMessage.id
            )
          } else {
            // start upload attachment
            socketStore.pushFileUploadStarted(
              message.conversationId,
              newMessage.id
            )
            await messageAttachmentsStore.createAttachment({
              file,
              messageId: newMessage.id,
            })
            // finish upload attachment
            socketStore.pushFileUploadFinished(
              message.conversationId,
              newMessage.id
            )
          }
        }

        this.sendMessageState = updateState(this.sendMessageState, {
          data: newMessage,
        })
      } catch (error) {
        const serverError = createError(ErrorTypes.SERVER)
        const messageError = isNetworkError(error as Error)
          ? error
          : serverError

        this.sendMessageState = updateState(
          this.sendMessageState,
          messageError as Error
        )
        throw messageError
      }
    },

    addMessageToCurrentConversation(message?: IMessage) {
      const conversationsStore = useConversationsStore()
      const videoCallConfigsStore = useVideoCallConfigsStore()
      const { isSmallScreen } = useWindowSize()

      const { data: existingList = [], meta } = this.messages.data ?? {}
      if (
        message &&
        message.conversationId === conversationsStore.conversation.data?.id &&
        message.conversationId === conversationsStore.currentConversationId
      ) {
        const mergedMessages = mergeMessages({
          array: [existingList, [message]],
        })
        this.messages = updateState(
          this.messages,
          new MessageIndexRO({
            meta,
            data: mergedMessages,
          })
        )

        if (
          isSmallScreen.value &&
          !videoCallConfigsStore.isPictureInPictureMode
        ) {
          useVideoCallNotifications(message)
        }
      }
    },

    handleSpecialMessages(message: IMessage) {
      if (
        message.messageType === MessageType.VIDEO_CHAT_DECLINE ||
        message.messageType === MessageType.SCREENSHARING_DECLINE
      ) {
        EventBus.emit(VideoCallEvents.RedirectToConversation)
      }
    },

    updateMessageById(targetId: number, message: IMessage) {
      const { data: existingList = [], meta } = this.messages.data ?? {}
      const newMessages = existingList.map((item) =>
        item.id === targetId ? message : item
      )

      this.messages = updateState(
        this.messages,
        new MessageIndexRO({
          meta,
          data: newMessages,
        })
      )
    },

    updateAttachmentsById(targetId: number, newAttachments: IAttachment[]) {
      const messageAttachment =
        mergeAttachmentToEmbeddedAttachment(newAttachments)
      if (messageAttachment) {
        const newMessages = this.messages.data?.data.map((item) => {
          if (item.id === targetId) {
            return { ...item, attachments: [messageAttachment] }
          }
          return item
        })

        const { data: existingList = [], meta } = this.messages.data ?? {}
        if (newMessages) {
          const mergedMessages = mergeMessages({
            array: [existingList, newMessages],
          })

          this.messages = updateState(
            this.messages,
            new MessageIndexRO({
              meta,
              data: mergedMessages,
            })
          )
        }
      }
    },

    updateQuickSignDocumentInMessage(quickSignDocument: IQuickSignDocument) {
      const quickSign = {
        id: quickSignDocument?.id,
        status: quickSignDocument?.status,
      }

      const { data: existingList = [], meta } = this.messages.data ?? {}
      const mergedMessages = updateQuickSignInMessage(
        existingList as Array<IMessage>,
        quickSign
      )

      this.messages = updateState(
        this.messages,
        new MessageIndexRO({
          meta,
          data: mergedMessages,
        })
      )
    },

    updateAiMessage(message: IMessage) {
      const { data: existingList = [], meta } = this.messages.data ?? {}
      const newMessages = existingList.map((item) =>
        item.id === message.id ? message : item
      )

      this.messages = updateState(
        this.messages,
        new MessagesIndexRO({
          meta,
          data: newMessages,
        })
      )
    },

    resetSendMessageState() {
      this.sendMessageState = getInitialState()
    },
  },
  getters: {
    messageList: (state) => state.messages.data?.data || [],
    isLastMessageLoaded: (state) =>
      state.messages.data?.meta?.page === state.messages.data?.meta?.totalPages,
    isLoadingResources: (state) => (uuid: string) =>
      state.loadingExternalLinks[uuid],
  },
})
